// Vuex module for Report Builder state
// This module mirrors the report object that is sent to the backend
// and provides simple mutations/getters to build that payload.
import userReportService from "@/helpers/api/userReport.service";
import { isReportConfigComplete } from "@/helpers/reportValidation";
import { classifyDateRangeType } from "@/helpers/dateRangeType";
import { buildSqlChartData, applySqlResult } from "@/helpers/reports/sqlChartAdapter";
import { withCategoryStyle } from "@/helpers/reports/categoryStyles";
import { buildReportPayload, fetchReportData, fetchSqlReportData } from "@/helpers/reports/executeReport";
import { hasUnresolvedRequiredParams } from "@/helpers/reports/requiredParams";
import { normalizeCompare, buildComparePayload, isCompareSupported, extractCompareAxis } from "@/helpers/reports/compare";
import { ElMessage } from 'element-plus';
import i18n from '@/i18n';

const templateRequest = () => ({
    type: 'trend',
    event_id: null,
    event_name: '',
    label: '',
    agr: 'total',
    agr_field: null,
    filters: [] // [{ key: 'lang', value: 'ru' }]
})

const templateTrendsRequest = () => ({
    type: 'trends',
    type_chart: 'line',
    requests: [templateRequest()],
    filters: null,
    separators: [],
    breakdown_limit: 25,
    granularity: 'day',
    formulas: [],   // array of { expression, label, color }; empty = disabled
    trends_utm_breakdown: false,
    utm_attribution: 'first',
    display_mode: 'hierarchical',
    row_limit: 100,
    sort_by: 0,
    sort_dir: 'desc',
    drill_path: null
})

const templateListRequest = () => ({
    type: 'list',
    type_chart: 'list',
    table: 'events',
    columns: ['events.date', 'events.user_id', 'events.event_type_id', 'events.event_name', 'events.value_int'],
    filters: null,
    order: { 'events.date': 'desc' },
    limit: 50,
    offset: 0
})

const templateFunnelsRequest = () => ({
    type: 'funnels',
    type_chart: 'funnels',
    requests: [
        { ...templateRequest(), event_id: 7, label: 'Step 1' },
        { ...templateRequest(), event_id: 11, label: 'Step 2' }
    ],
    filters: null,
    separators: [],
    breakdown_limit: 25,
    funnel_window: 14,
    funnel_window_unit: 'day',
    funnel_order_type: 'strict',
    funnel_conversion_mode: 'overall',
    funnel_utm_breakdown: false,
    utm_attribution: 'first',
    drill_path: null,
    row_limit: 1000
})

const templateUtmRequest = () => ({
    type: 'utm',
    type_chart: 'utm_trends',
    utm_attribution: 'first',
    display_mode: 'hierarchical',
    row_limit: 100,
    sort_by: 0,
    sort_dir: 'desc',
    granularity: 'day',
    requests: [templateRequest()],
    filters: null,
    drill_path: []
})

const templateRetentionRequest = () => ({
    type: 'retention',
    type_chart: 'retention',
    granularity: 'day',
    retention_total_intervals: 8,
    retention_type: 'retention_first_time',
    mean_retention_calculation: 'simple',
    target_entity: {
        type: 'event',
        event_id: null,
        event_name: '',
        filters: []
    },
    returning_entity: {
        type: 'event',
        event_id: null,
        event_name: '',
        filters: []
    },
    filters: null,
    separators: [],
    retention_utm_breakdown: false,
    utm_attribution: 'first',
    drill_path: null
})

const templatePathsRequest = () => ({
    type: 'paths',
    type_chart: 'paths',
    // Unit a path is built over: 'session' | 'user' | 'app_session'
    path_source: 'session',
    max_steps: 5,
    // Optional anchors: { event_id, event_name } | null
    path_start: null,
    path_end: null,
    // Event ids dropped from every path
    path_exclusions: [],
    // [{ alias, event_ids: [...], event_names: [...] }] — collapse several events into one node
    path_groupings: [],
    filters: null,
    // Max number of edges (transitions) fetched. The backend keeps the trunk (early steps)
    // first, so this prunes the deepest leaves rather than disconnecting the graph.
    row_limit: 300
})

// SQL-режим: тот же конверт, что у остальных отчётов. `sql` заменяет собой описание
// запроса, а визуализация описывается ровно теми же полями, что в трендах:
// requests[] = серии (только вместо события — колонка), separators = разбивка,
// breakdown_limit = сколько разбивок показывать. Новое здесь одно поле — `x`:
// в трендах ось X всегда дата, а в SQL её нужно назвать явно.
const templateSqlRequest = () => ({
    type: 'sql',
    type_chart: 'table',
    sql: "SELECT formatDateTime(events.date, 'auto') AS day, count(*) AS total\n" +
         "FROM events\n" +
         "WHERE events.date BETWEEN :date_from AND :date_to\n" +
         "GROUP BY day\n" +
         "ORDER BY day ASC\n" +
         "LIMIT 100",
    x: null,
    requests: [],
    separators: [],
    breakdown_limit: 25,
    row_limit: 1000
})

// Пересобирает chartData из уже полученных строк — без похода на бэкенд.
// Дёргается из мутаций, меняющих визуализацию (тип графика, оси, разбивка).
const rebuildSqlChartData = (state) => {
    if (state.type !== 'sql') return
    state.chartData = buildSqlChartData(
        state.sqlRows,
        state.source_request,
        state.type_chart,
        { otherLabel: i18n.global.t('ReportBuilder.sqlTab.otherCategory') }
    )
}

const getDefaultDates = () => {
    return {
        from: '-30d',
        to: 'now'
    }
}

const defaultState = {
    // High-level report descriptor
    type: 'trends',

    // Full source request used by backend to generate the report
    source_request: templateTrendsRequest(),

    // Optional query object (reserved for future use / backend compatibility)
    query: {},

    // Data for charts (frontend side). Typically filled by backend response.
    chartData: null,

    // Top-level chart type for UI rendering convenience
    // Keep in sync with source_request.type_chart
    type_chart: 'line',

    // Visualization options (frontend)
    visual: {},

    // Settings for backend params like date range, resource keys, etc.
    settings: {
        params: {
            ':date_from': getDefaultDates().from,
            ':date_to': getDefaultDates().to,
            ':resource_key': ['$current_resource']
        }
    },
    total_count: 0,
    hiddenDatasets: [],
    loading: false,
    // Сравнение периодов: строки второго запроса и его фактический период.
    // Сам конфиг сравнения живёт в visual.compare (сохраняется вместе с отчётом),
    // здесь — только результат последнего запроса.
    compareData: null,
    comparePeriod: null,
    compareLoading: false,
    // SQL-режим: сырые строки сравнительного периода (для дельт в таблице результата)
    compareSqlRows: [],
    utmActiveMetric: 0,
    // Extra flat rows added by UTM drill-down: { [expandKey]: flatRow[] }
    // Chart merges this with chartData to show child UTM lines.
    utmExpandedRows: {},
    // SQL-режим: сырые строки последнего выполнения. График строится из них локально,
    // поэтому смена типа/осей перерисовывает отчёт без повторного запроса.
    sqlRows: [],
    sqlColumns: [],
    sqlErrors: [],
    // Custom query support: set when a report uses report_request instead of template builder
    reportUuid: null,
    isCustomQuery: false,
    // True only right after a saved report was loaded with literal (non-relative) dates baked
    // into settings.params — drives the "hardcoded dates" warning. Cleared as soon as the user
    // interacts with the date range selector, so it never fires for dates picked in-session.
    hardcodedDateOnLoad: false,
}

export default {
    namespaced: true,
    state: { ...defaultState },

    getters: {
        // Strip internal-only keys (prefixed with _) from source_request before sending to backend
        cleanSourceRequest: (state) => {
            const sr = state.source_request
            if (!sr) return sr
            const cleaned = {}
            for (const key of Object.keys(sr)) {
                if (!key.startsWith('_')) cleaned[key] = sr[key]
            }
            return cleaned
        },
        // Returns the full payload expected by backend
        payload(state, getters) {
            return {
                type: state.type,
                source_request: getters.cleanSourceRequest,
                query: state.query,
                type_chart: state.type_chart,
                visual: state.visual,
                settings: state.settings,
            }
        },
        // Configuration only (excludes chartData/total_count)
        reportConfig(state, getters) {
            return {
                type: state.type,
                source_request: getters.cleanSourceRequest,
                query: state.query,
                type_chart: state.type_chart,
                visual: state.visual,
                settings: state.settings,
            }
        },
        // Shortcuts
        requests: (state) => state.source_request?.requests || [],
        params: (state) => state.settings?.params || {},
        total_count: (state) => state.total_count || 0,
    },

    actions: {
        // Convenience action to get payload (same as getter)
        buildPayload({ getters }) {
            return getters.payload
        },
        // Load an incoming report object straight into state
        importPayload({ commit }, payload) {
            commit('importPayload', payload)
        },
        async loadDataForReport({ commit, dispatch, state, rootState }) {
            const payload = buildReportPayload(state, { currentBotKey: rootState.init?.current_bot?.key })

            commit('setLoading', true)

            // SQL path: свой эндпоинт, результат — плоские строки, график собирается на клиенте
            if (state.type === 'sql') {
                commit('clearCompareData')
                const sql = payload.source_request?.sql
                if (!sql || !sql.trim()) {
                    commit('setLoading', false)
                    return
                }
                return fetchSqlReportData(payload)
                    .then(data => {
                        commit('setSqlResult', data)
                        // Сравнение считаем после основного результата: он мог автоподобрать
                        // маппинг (x/метрики/разбивка), а строки сравнения должны лечь в тот же
                        dispatch('loadCompareData', payload)
                    })
                    .catch(e => {
                        commit('setSqlErrors', e)
                    })
                    .finally(() => {
                        commit('setLoading', false)
                    })
            }

            // Custom query path: delegate to user-report/execute with current settings
            if (state.isCustomQuery && state.reportUuid) {
                commit('clearCompareData')
                return userReportService.execute(state.reportUuid, { settings: payload.settings }).then(data => {
                    commit('setChartData', data)
                }).catch(e => {
                    console.log(e)
                }).finally(() => {
                    commit('setLoading', false)
                })
            }

            // Standard template path
            if (!isReportConfigComplete(payload)) {
                console.log('Skipping report generation: filters are incomplete');
                ElMessage.warning(i18n.global.t('ReportBuilder.messages.incompleteConfig'));
                commit('setLoading', false)
                return;
            }

            // A `:param` placeholder (see helpers/reports/requiredParams.js) with no preview
            // value yet — e.g. still being typed into the variable-name input — would always
            // 500 on the backend. Skip silently, same as any other still-being-edited config;
            // the request fires once a value is picked in the Report Variables panel.
            if (hasUnresolvedRequiredParams(payload.source_request, payload.settings?.params)) {
                commit('setLoading', false)
                return;
            }

            return fetchReportData(payload).then(data => {
                commit('setChartData', data)
                // Запрос сравнения — вдогонку, чтобы основной график не ждал второй ответ
                dispatch('loadCompareData', payload)
            }).catch(e => {
                console.log(e)
            }).finally(() => {
                commit('setLoading', false)
            })
        },

        /**
         * Второй запрос того же отчёта с патчем поверх базового payload
         * (сейчас — другой период; сюда же лягут сегменты/когорты через compare.overrides).
         */
        async loadCompareData({ commit, state }, basePayload) {
            const compare = normalizeCompare(state.visual)
            if (!compare || !isCompareSupported(state.type, state.type_chart)) {
                commit('clearCompareData')
                return
            }

            // Ось основного ответа — источник правды для сдвига периода (у SQL ось X
            // произвольная, не обязательно датная, поэтому там её не берём)
            const axis = extractCompareAxis(state, state.chartData)
            const built = buildComparePayload(basePayload, compare, axis)
            if (!built) {
                commit('clearCompareData')
                return
            }

            const period = { from: built.period.from, to: built.period.to, shift: built.period.shift }
            commit('setCompareLoading', true)
            try {
                if (state.type === 'sql') {
                    const rows = await fetchSqlReportData(built.payload)
                    commit('setCompareSqlResult', { response: rows, period })
                } else {
                    const data = await fetchReportData(built.payload)
                    commit('setCompareData', { data, period })
                }
            } catch (e) {
                console.log(e)
                commit('clearCompareData')
            } finally {
                commit('setCompareLoading', false)
            }
        }

    },

    mutations: {
        // Generic shallow set (top-level only)
        __set(state, { name, val }) {
            state[name] = val
        },

        // Reset to defaults
        resetData(state) {
            state.type = defaultState.type;
            state.source_request = templateTrendsRequest();
            state.query = {};
            state.chartData = null;
            state.type_chart = 'line';
            state.visual = {};
            state.settings = {
                params: {
                    ':date_from': getDefaultDates().from,
                    ':date_to': getDefaultDates().to,
                    ':resource_key': ['$current_resource']
                }
            };
            state.total_count = 0;
            state.hiddenDatasets = [];
            state.loading = false;
            state.compareData = null;
            state.comparePeriod = null;
            state.compareSqlRows = [];
            state.compareLoading = false;
            state.utmActiveMetric = 0;
            state.utmExpandedRows = {};
            state.sqlRows = [];
            state.sqlColumns = [];
            state.sqlErrors = [];
            state.reportUuid = null;
            state.isCustomQuery = false;
            state.hardcodedDateOnLoad = false;
        },

        setCustomQuery(state, { uuid, isCustom }) {
            state.reportUuid = uuid || null;
            state.isCustomQuery = !!isCustom;
        },

        // Import full payload
        importPayload(state, payload) {
            // The top-level `type`/`type_chart` envelope is optional: the report engine only ever
            // reads `source_request`, so a config saved via API/MCP/AI as a bare `{source_request,
            // settings}` is valid. Fall back to `source_request` here so such a config renders with
            // its real chart type instead of the store default 'line'. (This is also why the row
            // backfill is no longer needed — envelope-less rows render correctly on load.)
            const sr = payload.source_request
            if (payload.type) state.type = payload.type;
            else if (sr?.type) state.type = sr.type;
            if (sr) {
                // Migrate legacy single formula → formulas array
                if (sr.formula !== undefined && !sr.formulas) {
                    sr.formulas = sr.formula ? [sr.formula] : []
                    delete sr.formula
                }
                if (!Array.isArray(sr.formulas)) sr.formulas = []
                state.source_request = sr
            }
            if (payload.query) state.query = payload.query;
            if (payload.type_chart) state.type_chart = payload.type_chart;
            else if (sr?.type_chart) state.type_chart = sr.type_chart;
            if (payload.visual) state.visual = payload.visual;
            if (payload.settings) state.settings = payload.settings;
            if (payload.total_count !== undefined) state.total_count = payload.total_count;
            // Purposely do not import chartData to force a refresh, or empty it out
            state.chartData = null;
            // Конфиг сравнения приходит внутри payload.visual, а его данные — нет
            state.compareData = null;
            state.comparePeriod = null;
            state.compareSqlRows = [];
            state.hiddenDatasets = [];
            state.utmExpandedRows = {};
            state.sqlRows = [];
            state.sqlColumns = [];
            state.sqlErrors = [];
            // Snapshot at load time only: a report saved with literal dates (rather than a
            // relative preset like -30d) silently goes stale once "today" moves past them.
            const loadedDf = state.settings?.params?.[':date_from'];
            const loadedDt = state.settings?.params?.[':date_to'];
            state.hardcodedDateOnLoad = classifyDateRangeType(loadedDf, loadedDt) === 'explicit';
        },

        // Dismiss the "hardcoded dates" warning once the user deliberately interacts with the
        // date range selector (picking a preset or editing dates is no longer "unnoticed").
        clearHardcodedDateWarning(state) {
            state.hardcodedDateOnLoad = false;
        },

        // Switch between high-level report types (trends, list, etc)
        switchReportType(state, type) {
            state.type = type
            state.chartData = null // Reset charts/tables so stale data isn't carried over!
            state.compareData = null
            state.comparePeriod = null
            state.compareSqlRows = []

            if (type === 'list') {
                state.source_request = templateListRequest()
                state.type_chart = 'list'
            } else if (type === 'funnels') {
                state.source_request = templateFunnelsRequest()
                state.type_chart = 'funnels'
            } else if (type === 'retention') {
                state.source_request = templateRetentionRequest()
                state.type_chart = 'retention'
            } else if (type === 'utm') {
                state.source_request = templateUtmRequest()
                state.type_chart = 'utm_table'
            } else if (type === 'paths') {
                state.source_request = templatePathsRequest()
                state.type_chart = 'paths'
            } else if (type === 'sql') {
                state.source_request = templateSqlRequest()
                state.type_chart = 'table'
                state.sqlRows = []
                state.sqlColumns = []
                state.sqlErrors = []
            } else {
                // default to trends
                state.source_request = templateTrendsRequest()
                state.type_chart = 'line'
            }
        },

        // Source request specific setters
        setSourceType(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.type = val
        },
        setTypeChart(state, val) {
            state.type_chart = val
            if (!state.source_request) state.source_request = {}
            state.source_request.type_chart = val
            rebuildSqlChartData(state)
        },
        setRequests(state, requests) {
            if (!state.source_request) state.source_request = {}
            state.source_request.requests = requests
            rebuildSqlChartData(state)
        },
        addRequest(state, req = {}) {
            if (!state.source_request) state.source_request = {}
            if (!Array.isArray(state.source_request.requests)) state.source_request.requests = []
            state.source_request.requests.push({ ...templateRequest(), ...req })
        },
        updateRequest(state, { index, patch }) {
            if (state.source_request?.requests?.[index]) {
                state.source_request.requests[index] = {
                    ...state.source_request.requests[index],
                    ...patch,
                };
                rebuildSqlChartData(state)
            }
        },
        removeRequest(state, index) {
            if (!state.source_request || !Array.isArray(state.source_request.requests)) return
            state.source_request.requests.splice(index, 1)
            rebuildSqlChartData(state)
        },

        setFilter(state, filters) {
            state.source_request.filters = filters
        },

        setSeparators(state, separators) {
            state.source_request.separators = separators
            rebuildSqlChartData(state)
        },

        setBreakdownLimit(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.breakdown_limit = val
            rebuildSqlChartData(state)
        },

        setGranularity(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.granularity = val
        },
        addFormula(state) {
            if (!state.source_request) state.source_request = {}
            if (!Array.isArray(state.source_request.formulas)) state.source_request.formulas = []
            const n = state.source_request.formulas.length + 1
            state.source_request.formulas.push({ expression: '', label: 'Formula ' + n, color: '#E6A23C' })
        },
        removeFormula(state, index) {
            if (!Array.isArray(state.source_request?.formulas)) return
            state.source_request.formulas.splice(index, 1)
        },
        updateFormula(state, { index, patch }) {
            if (!Array.isArray(state.source_request?.formulas)) return
            if (index < 0 || index >= state.source_request.formulas.length) return
            state.source_request.formulas[index] = { ...state.source_request.formulas[index], ...patch }
        },
        setFormulas(state, formulas) {
            if (!state.source_request) state.source_request = {}
            state.source_request.formulas = Array.isArray(formulas) ? formulas : []
        },

        // Retention specific setters
        setRetentionTotalIntervals(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.retention_total_intervals = val
        },
        setRetentionType(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.retention_type = val
        },
        setMeanRetentionCalculation(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.mean_retention_calculation = val
        },
        updateTargetEntity(state, patch) {
            if (!state.source_request.target_entity) {
                state.source_request.target_entity = { type: 'event', event_id: null, event_name: '', filters: [] }
            }
            state.source_request.target_entity = { ...state.source_request.target_entity, ...patch }
        },
        updateReturningEntity(state, patch) {
            if (!state.source_request.returning_entity) {
                state.source_request.returning_entity = { type: 'event', event_id: null, event_name: '', filters: [] }
            }
            state.source_request.returning_entity = { ...state.source_request.returning_entity, ...patch }
        },

        // List specific setters
        setListTable(state, table) {
            state.source_request.table = table
        },
        setListColumns(state, columns) {
            state.source_request.columns = columns
        },
        setListOrder(state, order) {
            state.source_request.order = order
        },
        setListLimit(state, limit) {
            state.source_request.limit = limit
        },
        setListOffset(state, offset) {
            state.source_request.offset = offset
        },

        // SQL specific setters
        setSqlText(state, sql) {
            if (!state.source_request) state.source_request = {}
            state.source_request.sql = sql
        },
        setSqlX(state, column) {
            if (!state.source_request) state.source_request = {}
            state.source_request.x = column || null
            rebuildSqlChartData(state)
        },
        // Результат выполнения: строки + автоподбор визуализации, если маппинг
        // ещё не задан или ссылается на колонки, которых больше нет в ответе.
        setSqlResult(state, response) {
            applySqlResult(state, response, { otherLabel: i18n.global.t('ReportBuilder.sqlTab.otherCategory') })
        },
        setSqlErrors(state, errors) {
            let list = []
            if (Array.isArray(errors)) list = errors.map(e => typeof e === 'string' ? e : JSON.stringify(e))
            else if (typeof errors === 'string') list = [errors]
            else if (errors && typeof errors === 'object' && !errors.server) list = Object.values(errors).flat().map(String)
            else list = [i18n.global.t('General.ErrorsText.ServerError')]

            state.sqlErrors = list
            state.sqlRows = []
            state.sqlColumns = []
            state.chartData = []
            state.total_count = 0
        },

        // Funnels specific setters
        setFunnelWindow(state, window) {
            if (!state.source_request) state.source_request = {}
            state.source_request.funnel_window = window
        },
        setFunnelWindowUnit(state, unit) {
            if (!state.source_request) state.source_request = {}
            state.source_request.funnel_window_unit = unit
        },
        setFunnelOrderType(state, orderType) {
            if (!state.source_request) state.source_request = {}
            state.source_request.funnel_order_type = orderType
        },
        setFunnelConversionMode(state, mode) {
            if (!state.source_request) state.source_request = {}
            state.source_request.funnel_conversion_mode = mode
        },
        setFunnelUtmBreakdown(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.funnel_utm_breakdown = !!val
            // Reset drill_path so a fresh report starts at the top UTM level.
            state.source_request.drill_path = val ? [] : null
        },

        setRetentionUtmBreakdown(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.retention_utm_breakdown = !!val
            // Сброс drill_path: включённая разбивка всегда стартует с верхнего
            // уровня меток, иначе отчёт откроется провалившимся неизвестно куда.
            state.source_request.drill_path = val ? [] : null
        },

        setUtmAttribution(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.utm_attribution = val
            // Пути в разных моделях атрибуции не совпадают: id уровня, взятый из
            // f_*-колонок, ничего не значит в l_*. Сбрасываем drill во всех
            // отчётах, где он вообще есть, а не только в ретеншене.
            if (Array.isArray(state.source_request.drill_path)) {
                state.source_request.drill_path = []
            }
        },

        setTrendsUtmBreakdown(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.trends_utm_breakdown = !!val
            // type_chart is left as-is: the flag alone is what tells the backend to run the
            // hierarchical UTM query (see QueryService::getService in www), so the report's
            // own line/bar chart type stays under the user's control the whole time — see
            // helpers/reports/utmTrends.js.
        },

        // UTM specific setters
        setDisplayMode(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.display_mode = val
        },
        setRowLimit(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.row_limit = val
        },
        setSortBy(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.sort_by = val
        },
        setSortDir(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.sort_dir = val
        },
        setDrillPath(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.drill_path = val
        },

        // Paths ("пользовательские пути") specific setters
        setPathSource(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.path_source = val
        },
        setMaxSteps(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.max_steps = val
        },
        setPathStart(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.path_start = val
        },
        setPathEnd(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.path_end = val
        },
        setPathExclusions(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.path_exclusions = Array.isArray(val) ? val : []
        },
        setPathGroupings(state, val) {
            if (!state.source_request) state.source_request = {}
            state.source_request.path_groupings = Array.isArray(val) ? val : []
        },

        // Filters within a specific request
        addRequestFilter(state, { index, filter }) {
            const req = state.source_request?.requests?.[index]
            if (!req) return
            if (!Array.isArray(req.filters)) req.filters = []
            req.filters.push(filter)
        },
        updateRequestFilter(state, { index, filterIndex, patch }) {
            const req = state.source_request?.requests?.[index]
            if (!req || !Array.isArray(req.filters)) return
            if (filterIndex < 0 || filterIndex >= req.filters.length) return
            req.filters[filterIndex] = { ...req.filters[filterIndex], ...patch }
        },
        removeRequestFilter(state, { index, filterIndex }) {
            const req = state.source_request?.requests?.[index]
            if (!req || !Array.isArray(req.filters)) return
            req.filters.splice(filterIndex, 1)
        },

        // Query
        setQuery(state, val) {
            state.query = val || {}
        },

        // Visual options
        setVisual(state, val) {
            state.visual = val || {}
        },
        // Оформление значения разбивки (цвет сектора/линии и своя подпись).
        // Хранится по значению, а не по индексу серии: при разбивке серий больше,
        // чем записей в requests, и порядок меняется при пересортировке данных.
        setCategoryStyle(state, { separators, patch }) {
            state.visual = withCategoryStyle(state.visual, separators, patch)
        },
        setVisualOption(state, { key, value }) {
            if (!state.visual) state.visual = {}
            state.visual = { ...state.visual, [key]: value }
        },
        updateRetentionColor(state, { date, color }) {
            if (!state.visual) state.visual = {}
            if (!state.visual.retention_colors) state.visual.retention_colors = {}
            state.visual.retention_colors[date] = color
        },
        updateRetentionCohortStyle(state, { key, patch }) {
            if (!state.visual) state.visual = {}
            if (!state.visual.retention_cohort_styles) state.visual.retention_cohort_styles = {}
            state.visual.retention_cohort_styles[key] = {
                ...(state.visual.retention_cohort_styles[key] || {}),
                ...patch
            }
        },

        setLoading(state, val) {
            state.loading = val
        },

        // Chart data (from backend)
        setChartData(state, val) {
            let rawData = [];
            if (val && typeof val === 'object' && !Array.isArray(val) && val.data !== undefined) {
                rawData = Array.isArray(val.data) ? val.data : []
                state.total_count = val.total_count || 0
            } else {
                rawData = Array.isArray(val) ? val : []
            }

            if (state.type === 'funnels' && rawData.length > 0) {
                // Keep the raw data for funnels to support breakdowns in the component
                state.chartData = rawData;
            } else {
                state.chartData = rawData;
            }
        },

        // --- Сравнение периодов ---
        // Конфиг (что с чем сравниваем) хранится в visual.compare и сохраняется вместе
        // с отчётом; здесь — только результат второго запроса.
        setCompare(state, patch) {
            const next = { ...(state.visual?.compare || {}), ...(patch || {}) }
            state.visual = { ...(state.visual || {}), compare: next }
            if (!next.enabled) {
                state.compareData = null
                state.comparePeriod = null
                state.compareSqlRows = []
            }
        },
        setCompareData(state, { data, period }) {
            let rows = []
            if (data && typeof data === 'object' && !Array.isArray(data) && data.data !== undefined) {
                rows = Array.isArray(data.data) ? data.data : []
            } else {
                rows = Array.isArray(data) ? data : []
            }
            state.compareData = rows
            state.comparePeriod = period || null
        },
        // SQL-режим: строки сравнительного периода + график из них по ТЕКУЩЕМУ маппингу
        // (x/метрики/разбивка уже подобраны основным результатом — переподбирать нельзя,
        // иначе два периода нарисовались бы по разным колонкам)
        setCompareSqlResult(state, { response, period }) {
            let rows = []
            if (response && typeof response === 'object' && !Array.isArray(response) && response.data !== undefined) {
                rows = Array.isArray(response.data) ? response.data : []
            } else {
                rows = Array.isArray(response) ? response : []
            }
            state.compareSqlRows = rows
            state.compareData = buildSqlChartData(
                rows,
                state.source_request,
                state.type_chart,
                { otherLabel: i18n.global.t('ReportBuilder.sqlTab.otherCategory') }
            )
            state.comparePeriod = period || null
        },
        clearCompareData(state) {
            state.compareData = null
            state.comparePeriod = null
            state.compareSqlRows = []
            state.compareLoading = false
        },
        setCompareLoading(state, val) {
            state.compareLoading = val
        },

        // Settings params (backend input placeholders)
        setSettingParam(state, { name, value }) {
            if (!state.settings) state.settings = { params: {} }
            if (!state.settings.params) state.settings.params = {}
            state.settings.params[name] = value
        },
        removeSettingParam(state, name) {
            if (!state.settings?.params) return
            const params = { ...state.settings.params }
            delete params[name]
            state.settings = { ...state.settings, params }
        },
        setSettings(state, val) {
            // replace entire settings object
            state.settings = val || { params: {} }
            if (!state.settings.params) state.settings.params = {}
        },
        toggleDatasetVisibility(state, index) {
            const pos = state.hiddenDatasets.indexOf(index);
            if (pos > -1) {
                state.hiddenDatasets.splice(pos, 1);
            } else {
                state.hiddenDatasets.push(index);
            }
        },
        setUtmActiveMetric(state, idx) {
            state.utmActiveMetric = idx
        },
        setHiddenDatasets(state, keys) {
            state.hiddenDatasets = Array.isArray(keys) ? [...keys] : []
        },
        // UTM drill-down: store/remove flat daily rows for child UTMs
        setUtmExpandedRows(state, { key, rows }) {
            state.utmExpandedRows = { ...state.utmExpandedRows, [key]: rows }
        },
        removeUtmExpandedRows(state, key) {
            const next = { ...state.utmExpandedRows }
            delete next[key]
            state.utmExpandedRows = next
        },
        resetUtmExpandedRows(state) {
            state.utmExpandedRows = {}
        }
    }
}

